import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useMQTTSensorData, globalMqttMessageStore } from '../../utils/mqttUtils';
import { useCards } from '../../utils/reactQueryHooks';
import { useCardData } from '../common/CardContext';
import { processTimeStamp } from '../../utils/chartUtils';
import '../../styles/mqttMessageViewer.css';

// Custom hook to capture MQTT messages
const useMqttMessageCapture = () => {
  const [mqttMessages, setMqttMessages] = useState(globalMqttMessageStore.getMessages());
  const { data: cards = [] } = useCards();
  const { cardData } = useCardData();

  // Subscribe to global message store
  useEffect(() => {
    const unsubscribe = globalMqttMessageStore.subscribe(setMqttMessages);
    return unsubscribe;
  }, []);

  // Use MQTT hook to listen for all messages
  const { isConnected, error } = useMQTTSensorData('all', null);

  // Get friendly device name from cards data
  const getFriendlyDeviceName = (macAddress) => {
    // First try to get from cards data (React Query)
    const card = cards.find(c => c.macAddress === macAddress);
    if (card?.sensorDescription) {
      return card.sensorDescription;
    }
    
    // Fallback to cardData context if available
    if (cardData[macAddress]?.sensorDescription) {
      return cardData[macAddress].sensorDescription;
    }
    
    // Final fallback
    return 'Unknown Device';
  };

  return { mqttMessages, isConnected, error, getFriendlyDeviceName };
};

const MqttMessageViewer = ({ 
  isOpen = false, 
  onToggle, 
  maxHeight = "400px",
  hideButton = false
}) => {
  const contentRef = useRef(null);
  const { mqttMessages, isConnected, error, getFriendlyDeviceName } = useMqttMessageCapture();
  
  // Filter states
  const [filters, setFilters] = useState({
    messageType: '',
    macAddress: '',
    deviceName: '',
    searchText: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  // Get unique values for filter dropdowns
  const getUniqueValues = (key) => {
    if (key === 'deviceName') {
      const values = mqttMessages.map(m => getFriendlyDeviceName(m.macAddress)).filter(Boolean);
      return [...new Set(values)].sort();
    }
    const values = mqttMessages.map(m => m[key]).filter(Boolean);
    return [...new Set(values)].sort();
  };

  const uniqueMessageTypes = getUniqueValues('messageType');
  const uniqueMacAddresses = getUniqueValues('macAddress');
  const uniqueDeviceNames = getUniqueValues('deviceName');

  // Filter messages based on active filters
  const filterMessages = (messages) => {
    return messages.filter(message => {
      // Message Type filter
      if (filters.messageType && message.messageType !== filters.messageType) {
        return false;
      }
      
      // MAC Address filter
      if (filters.macAddress && message.macAddress !== filters.macAddress) {
        return false;
      }
      
      // Device Name filter
      if (filters.deviceName && getFriendlyDeviceName(message.macAddress) !== filters.deviceName) {
        return false;
      }
      
      // Search text filter (searches across all fields)
      if (filters.searchText) {
        const searchLower = filters.searchText.toLowerCase();
        const matchesSearch = 
          (message.messageType && message.messageType.toLowerCase().includes(searchLower)) ||
          (message.macAddress && message.macAddress.toLowerCase().includes(searchLower)) ||
          (getFriendlyDeviceName(message.macAddress) && getFriendlyDeviceName(message.macAddress).toLowerCase().includes(searchLower)) ||
          (message.temperature && message.temperature.toString().includes(searchLower)) ||
          (message.humidity && message.humidity.toString().includes(searchLower)) ||
          (message.light && message.light.toString().includes(searchLower)) ||
          (message.distance && message.distance.toString().includes(searchLower));
        
        if (!matchesSearch) {
          return false;
        }
      }
      
      return true;
    });
  };

  // Apply filters
  const displayMessages = filterMessages(mqttMessages);

  // Check if any filters are active
  const hasActiveFilters = Object.values(filters).some(value => value !== '');

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      messageType: '',
      macAddress: '',
      deviceName: '',
      searchText: ''
    });
  };

  // Update individual filter
  const updateFilter = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Auto-scroll to top when messages change
  useEffect(() => {
    if (isOpen && contentRef.current && mqttMessages.length === 1) {
      // Only auto-scroll to top for the very first message
      contentRef.current.scrollTop = 0;
    }
  }, [isOpen, mqttMessages.length]);

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    try {
      // Handle different timestamp formats (Date object, string, number)
      let dateObj;
      
      if (timestamp instanceof Date) {
        // Already a Date object
        dateObj = timestamp;
      } else if (typeof timestamp === 'string') {
        // Parse string to Date
        dateObj = new Date(timestamp);
      } else if (typeof timestamp === 'number') {
        // Convert number (timestamp) to Date
        dateObj = new Date(timestamp);
      } else {
        // Fallback: try to create Date from whatever it is
        dateObj = new Date(timestamp);
      }
      
      // Validate the date is valid
      if (isNaN(dateObj.getTime())) {
        // Invalid date, return fallback
        return 'Invalid Date';
      }
      
      // Use ISO string for consistent parsing
      return processTimeStamp(dateObj.toISOString());
    } catch (error) {
      console.error('Error formatting timestamp:', error, timestamp);
      // Try fallback formatting
      try {
        if (timestamp instanceof Date) {
          return timestamp.toLocaleTimeString();
        }
        const fallbackDate = new Date(timestamp);
        if (!isNaN(fallbackDate.getTime())) {
          return fallbackDate.toLocaleTimeString();
        }
      } catch (e) {
        // Last resort
      }
      return 'Invalid Date';
    }
  };

  // Get message type color class
  const getMessageTypeClass = (messageType) => {
    switch (messageType?.toLowerCase()) {
      case 'sensor':
        return 'message-type-sensor';
      case 'actuator':
        return 'message-type-actuator';
      default:
        return 'message-type-default';
    }
  };

  // Handle drag start for potential future drag-to-close functionality
  const handleDragStart = (e) => {
    if (e.target.closest('.mqtt-table') || e.target.closest('.mqtt-header')) {
      return; // Don't start drag if clicking on table or header
    }
  };

  return (
    <div className="mqtt-viewer-container">
      {/* Toggle Button - Only show when panel is closed and other modals are not open */}
      {!isOpen && !hideButton && (
        <button 
          className="mqtt-toggle-button"
          onClick={onToggle}
          aria-label="Open MQTT viewer"
          title="Open MQTT viewer"
        >
          <i className={`ri-wifi-${isConnected ? 'line' : 'off-line'}`}></i>
          {mqttMessages.length > 0 && (
            <span className="mqtt-badge">{mqttMessages.length}</span>
          )}
          {!isConnected && (
            <span className="mqtt-connection-indicator offline"></span>
          )}
        </button>
      )}

      {/* Console Panel */}
      <div 
        className={`mqtt-panel ${isOpen ? 'open' : ''}`}
        style={{ maxHeight: isOpen ? maxHeight : '0px' }}
        onMouseDown={handleDragStart}
      >
        {/* Header */}
        <div className="mqtt-header">
          <div className="mqtt-header-content">
            <h3 className="mqtt-title">
              <i className="ri-wifi-line"></i>
              Live MQTT Messages
            </h3>
            <div className="mqtt-header-actions">
              <button 
                className="mqtt-filter-toggle-btn"
                onClick={() => setShowFilters(!showFilters)}
                aria-label="Toggle filters"
                title={showFilters ? "Hide filters" : "Show filters"}
              >
                <i className={`ri-filter-${showFilters ? 'fill' : 'line'}`}></i>
                {hasActiveFilters && <span className="filter-active-indicator"></span>}
              </button>
              <span className="mqtt-count">
                {displayMessages.length} {hasActiveFilters ? 'filtered' : 'of'} {mqttMessages.length}
              </span>
              <span className={`mqtt-connection-status ${isConnected ? 'connected' : 'disconnected'}`}>
                <i className={`ri-wifi-${isConnected ? 'line' : 'off-line'}`}></i>
                {isConnected ? 'Connected' : 'Disconnected'}
              </span>
              <button 
                className="mqtt-close-btn"
                onClick={onToggle}
                aria-label="Close MQTT viewer"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
          </div>
          <div className="mqtt-drag-handle">
            <div className="drag-indicator"></div>
          </div>
        </div>

        {/* Filters Section */}
        {showFilters && (
          <div className="mqtt-filters">
            <div className="filters-row">
              <div className="filter-group">
                <label htmlFor="filter-message-type">
                  <i className="ri-message-3-line"></i> Message Type
                </label>
                <select
                  id="filter-message-type"
                  value={filters.messageType}
                  onChange={(e) => updateFilter('messageType', e.target.value)}
                  className="filter-select"
                >
                  <option value="">All Types</option>
                  {uniqueMessageTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label htmlFor="filter-mac">
                  <i className="ri-barcode-line"></i> MAC Address
                </label>
                <select
                  id="filter-mac"
                  value={filters.macAddress}
                  onChange={(e) => updateFilter('macAddress', e.target.value)}
                  className="filter-select"
                >
                  <option value="">All Addresses</option>
                  {uniqueMacAddresses.map(mac => (
                    <option key={mac} value={mac}>{mac}</option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label htmlFor="filter-device">
                  <i className="ri-device-line"></i> Device Name
                </label>
                <select
                  id="filter-device"
                  value={filters.deviceName}
                  onChange={(e) => updateFilter('deviceName', e.target.value)}
                  className="filter-select"
                >
                  <option value="">All Devices</option>
                  {uniqueDeviceNames.map(device => (
                    <option key={device} value={device}>{device}</option>
                  ))}
                </select>
              </div>

              <div className="filter-group filter-search">
                <label htmlFor="filter-search">
                  <i className="ri-search-line"></i> Search
                </label>
                <div className="filter-search-row">
                  <input
                    id="filter-search"
                    type="text"
                    value={filters.searchText}
                    onChange={(e) => updateFilter('searchText', e.target.value)}
                    placeholder="Search messages..."
                    className="filter-input"
                  />
                  {hasActiveFilters && (
                    <button 
                      className="clear-filters-btn"
                      onClick={clearFilters}
                      title="Clear all active filters"
                    >
                      <i className="ri-close-circle-line"></i>
                      Clear All
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        <div 
          className="mqtt-content"
          ref={contentRef}
          tabIndex={0}
        >
          {error ? (
            <div className="mqtt-error">
              <i className="ri-error-warning-line"></i>
              <p>Connection Error: {error.message}</p>
            </div>
          ) : displayMessages.length > 0 ? (
            <div className="mqtt-table-container">
              <table className="mqtt-table">
                <thead>
                  <tr>
                    <th>⏱️ Timestamp</th>
                    <th>🧠 Message Type</th>
                    <th>🏷️ MAC Address</th>
                    <th>📛 Device Name</th>
                    <th>🌡️ Temperature</th>
                    <th>💧 Humidity</th>
                    <th>💡 Light</th>
                    <th>💧 Water Level</th>
                  </tr>
                </thead>
                <tbody>
                  {displayMessages.map((message) => (
                    <tr key={message.id} className="mqtt-row">
                      <td className="mqtt-time">
                        {formatTimestamp(message.timestamp)}
                      </td>
                      <td className="mqtt-message-type">
                        <span className={`message-type-badge ${getMessageTypeClass(message.messageType)}`}>
                          {message.messageType}
                        </span>
                      </td>
                      <td className="mqtt-mac">
                        <code>{message.macAddress}</code>
                      </td>
                      <td className="mqtt-device-name">
                        {getFriendlyDeviceName(message.macAddress)}
                      </td>
                      <td className="mqtt-temperature">
                        {message.temperature ? `${message.temperature.toFixed(1)}°F` : '-'}
                      </td>
                      <td className="mqtt-humidity">
                        {message.humidity ? `${message.humidity.toFixed(1)}%` : '-'}
                      </td>
                      <td className="mqtt-light">
                        {message.light ? `${message.light.toFixed(1)} lux` : '-'}
                      </td>
                      <td className="mqtt-distance">
                        {message.distance ? `${(message.distance / 2.54).toFixed(1)} in` : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="mqtt-empty">
              <i className="ri-wifi-off-line"></i>
              <p>No MQTT messages received yet...</p>
              <small>Messages will appear here as they are received</small>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

MqttMessageViewer.propTypes = {
  isOpen: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  maxHeight: PropTypes.string,
  hideButton: PropTypes.bool
};

export default MqttMessageViewer; 